import React, { useEffect, useMemo, useState, type CSSProperties } from "react";
import { FaRegCopy } from "react-icons/fa";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  checkPurchaseStatus,
  selectTransferPayment,
  startPolling,
  stopPolling,
} from "../../features/store/transferPaymentSlice";
import { formatCurrency } from "../../utils/utils";

interface PaymentDetailsProps {
  paymentDetails?: {
    bank?: string;
    bank_name?: string;
    account_number?: string;
    account_name?: string;
    amount?: number;
    reference?: string;
    accountExpiry?: string;
  };
  onProcessingFallback?: () => void;
  onSuccess?: () => void;
}

const PaymentDetails: React.FC<PaymentDetailsProps> = ({
  paymentDetails,
  onProcessingFallback,
  onSuccess,
}) => {
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);

  const dispatch = useAppDispatch();
  const { paymentDetails: storedDetails, transactionStatus } = useAppSelector(
    selectTransferPayment,
  );
  const details = useMemo(
    () => paymentDetails || storedDetails,
    [paymentDetails, storedDetails],
  );
  const normalizedTransactionStatus = transactionStatus?.trim().toUpperCase();
  const hasResolvedPastPending =
    !!normalizedTransactionStatus &&
    !["PENDING", "AWAITINGPAYMENT"].includes(normalizedTransactionStatus);
  const isSuccessfulStatus = normalizedTransactionStatus === "SUCCESSFUL";

  const CountdownTimer = ({ targetTime }: { targetTime: string }) => {
    const [timeLeft, setTimeLeft] = useState("00:00");

    useEffect(() => {
      const target = new Date(targetTime).getTime();
      const interval = setInterval(() => {
        const now = Date.now();
        const diff = target - now;

        if (diff <= 0) {
          setTimeLeft("00:00");
          clearInterval(interval);
          return;
        }

        const totalSeconds = Math.floor(diff / 1000);
        const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
        const seconds = String(totalSeconds % 60).padStart(2, "0");

        setTimeLeft(`${minutes}:${seconds}`);
      }, 1000);

      return () => clearInterval(interval);
    }, [targetTime]);

    return <>{timeLeft}</>;
  };

  useEffect(() => {
    if (!details?.reference) return;
    if (isSuccessfulStatus) {
      dispatch(stopPolling());
      return;
    }

    dispatch(startPolling());

    const interval = setInterval(() => {
      dispatch(checkPurchaseStatus(details.reference as string));
    }, 5000);

    return () => {
      clearInterval(interval);
      dispatch(stopPolling());
    };
  }, [dispatch, details?.reference, isSuccessfulStatus]);

  useEffect(() => {
    if (
      !hasResolvedPastPending ||
      isSuccessfulStatus ||
      !onProcessingFallback
    ) {
      return;
    }

    const timeout = window.setTimeout(() => {
      onProcessingFallback();
    }, 20000);

    return () => window.clearTimeout(timeout);
  }, [hasResolvedPastPending, isSuccessfulStatus, onProcessingFallback]);

  useEffect(() => {
    if (!isSuccessfulStatus || !onSuccess) {
      return;
    }

    onSuccess();
  }, [isSuccessfulStatus, onSuccess]);

  useEffect(() => {
    if (hasResolvedPastPending) {
      setSent(true);
    }
  }, [hasResolvedPastPending]);

  useEffect(() => {
    if (!copied) return;

    const timeout = window.setTimeout(() => {
      setCopied(false);
    }, 1500);

    return () => window.clearTimeout(timeout);
  }, [copied]);

  const handleCopyAccountNumber = async () => {
    const accountNumber = details?.account_number;

    if (!accountNumber) return;

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(accountNumber);
        setCopied(true);
        toast.success("Account number copied.");
        return;
      } catch (error) {
        // Fall back to manual copy below.
      }
    }

    const textarea = document.createElement("textarea");
    textarea.value = accountNumber;
    textarea.setAttribute("readonly", "true");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    setCopied(true);
    toast.success("Account number copied.");
  };

  if (!details) return null;

  return (
    <>
      {sent ? (
        <div className=" dark:text-white">
          <p className="font-medium text-sm text-dark-800 dark:text-gray-100 md:text-base text-center">
            <b>
              {hasResolvedPastPending
                ? "Payment received!"
                : "Receiving your transfer..."}
            </b>
          </p>
          <div className="flex items-center justify-center h-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
          </div>
          <p className="font-satoshi font-medium text-sm text-dark-700  md:text-base text-center">
            {hasResolvedPastPending
              ? "Processing your order..."
              : "You can leave this page, we will notify you once the payment is received"}
          </p>
        </div>
      ) : (
        <>
          <div className="font-satoshi dark:text-white">
            <p className="font-semibold text-sm lg:text-base ">
              Payment Details
            </p>
            <p className="pt-3 pb-3">
              Transfer the payment into the bank details below
            </p>
            <p className=""> Bank </p>
            <p className="font-semibold pb-2">
              {details?.bank_name || details?.bank || "Bank Name"}
            </p>

            <p> Account Number </p>
            <div className="flex items-center gap-2 pb-2">
              <p className="font-semibold m-0">
                {details?.account_number || "0000000000"}
              </p>
              {!!details?.account_number && (
                <button
                  type="button"
                  onClick={handleCopyAccountNumber}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                  aria-label="Copy account number"
                  title="Copy account number"
                >
                  <FaRegCopy size={12} />
                  <span>{copied ? "Copied" : "Copy"}</span>
                </button>
              )}
            </div>

            <p> Account Name </p>
            <p className="font-semibold pb-2">
              {details?.account_name || "Acct Name"}
            </p>

            <p> Amount </p>
            <p className="font-semibold pb-2">
              ₦{formatCurrency(details?.amount || 0)}
            </p>
          </div>

          <p className="text-red-500 text-center m-0 p-0">
            <b> This account is for this transaction only.</b>
          </p>
          <p className="font-medium text-sm  text-center m-0 p-0  dark:text-white">
            Expires in{" "}
            <b>
              {details?.accountExpiry && (
                <CountdownTimer targetTime={details?.accountExpiry} />
              )}
            </b>
          </p>
          <p className="text-center p-3">
            <button
              className=" px-4 py-2 text-[10px] bp:text-sm font-semibold text-white rounded-lg bg-gradient-to-b from-[#039EF2] to-[#027EC2]"
              onClick={() => setSent(true)}
            >
              I've sent the money
            </button>
          </p>
        </>
      )}
    </>
  );
};

const styles: { container: CSSProperties; arrow: CSSProperties } = {
  container: {
    width: "100%",
    height: "100px",
    display: "flex",
    alignItems: "center",
  },
  arrow: {
    fontSize: "30px",
    position: "absolute",
    animation: "moveArrow 1.5s linear infinite",
  },
};
export default PaymentDetails;
